import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

/**
 * CompetitorMoveSimulator - Phase 8.1
 *
 * Simulates "What if competitor changes their price by X%?"
 * Shows impact on your volume, share, and revenue using cross-elasticity.
 *
 * This is different from the competitor REACTION scenario in PricingSimulator:
 * - PricingSimulator: "I change my price, competitor reacts"
 * - CompetitorMoveSimulator: "Competitor changes their price independently"
 */

interface CompetitorMoveSimulatorProps {
  clientId: string;
  language?: 'en' | 'es';
}

interface Competitor {
  id: string;
  name: string;
  share: number;
  currentPrice?: number;
  growth?: number;
  manufacturer?: string;
}

// Default competitors if API doesn't return them
const DEFAULT_COMPETITORS: Competitor[] = [
  { id: 'coca-cola', name: 'Coca-Cola', share: 0.35, currentPrice: 25, growth: 0.02 },
  { id: 'pepsi', name: 'Pepsi', share: 0.22, currentPrice: 23, growth: -0.01 },
  { id: 'private-label', name: 'Private Label', share: 0.15, currentPrice: 18, growth: 0.05 },
  { id: 'other', name: 'Other Brands', share: 0.18, currentPrice: 20, growth: 0.01 },
];

// Cross-elasticity coefficients by competitor type
// Positive = substitutes (competitor price up → your volume up)
// Higher value = stronger substitution effect
const CROSS_ELASTICITY_BY_TYPE: Record<string, number> = {
  'direct': 0.6,      // Direct competitors (same segment)
  'premium': 0.4,     // Premium competitors
  'value': 0.5,       // Value competitors
  'private-label': 0.7, // Private label (high substitution)
  'default': 0.5,
};

export function CompetitorMoveSimulator({ clientId, language = 'en' }: CompetitorMoveSimulatorProps) {
  const t = language === 'es';

  const [selectedCompetitor, setSelectedCompetitor] = useState<Competitor | null>(null);
  const [priceChange, setPriceChange] = useState(0);

  // Fetch market overview for competitor data
  const { data: marketData, isLoading: marketLoading } = useQuery({
    queryKey: ['client', clientId, 'market', 'overview'],
    queryFn: () => apiClient.market.getOverview(clientId),
    enabled: !!clientId,
  });

  // Fetch your portfolio position for context
  const { data: positionData } = useQuery({
    queryKey: ['client', clientId, 'market', 'position'],
    queryFn: () => apiClient.market.getPosition(clientId),
    enabled: !!clientId,
  });

  // Transform API brands to competitors
  const competitors: Competitor[] = useMemo(() => {
    if (!marketData?.top_brands?.length) return DEFAULT_COMPETITORS;

    return marketData.top_brands.slice(0, 6).map((brand, idx) => ({
      id: brand.name.toLowerCase().replace(/\s+/g, '-'),
      name: brand.name,
      share: brand.share,
      growth: brand.growth,
      manufacturer: brand.manufacturer,
      // Estimate price based on share (larger share = often lower price in CPG)
      currentPrice: Math.round(20 + (1 - brand.share) * 15),
    }));
  }, [marketData]);

  // Your portfolio share from position data
  const yourShare = positionData?.total_share || 0.10;
  const yourBaseVolume = 1000; // Normalized base volume
  const yourBaseRevenue = yourBaseVolume * 22; // Assumed average price

  // Calculate impact of competitor price change on your portfolio
  const impact = useMemo(() => {
    if (!selectedCompetitor || priceChange === 0) {
      return {
        volumeChange: 0,
        shareChange: 0,
        revenueChange: 0,
        competitorNewShare: selectedCompetitor?.share || 0,
        narrative: null,
      };
    }

    // Determine cross-elasticity based on competitor type
    const competitorType = selectedCompetitor.name.toLowerCase().includes('private') ? 'private-label' : 'default';
    const crossElasticity = CROSS_ELASTICITY_BY_TYPE[competitorType] || 0.5;

    // Impact calculation:
    // If competitor raises price (priceChange > 0), your volume increases (cross-elasticity positive)
    // If competitor lowers price (priceChange < 0), your volume decreases
    const volumeChangePercent = crossElasticity * priceChange;

    // Share dynamics: your share change depends on competitor's share and the switch
    // Larger competitors switching creates bigger impact
    const shareMultiplier = selectedCompetitor.share / 0.3; // Normalize to 30% share
    const shareChangePoints = (volumeChangePercent / 100) * shareMultiplier * 2; // Convert to pp

    // Revenue change = volume change (assuming you keep your price)
    const revenueChangePercent = volumeChangePercent;

    // Competitor's new share (inverse effect)
    // If they raise price, they lose share; if they lower, they gain
    const ownPriceElasticity = -1.5; // Typical for CPG
    const competitorVolumeChange = ownPriceElasticity * priceChange;
    const competitorShareChange = (competitorVolumeChange / 100) * selectedCompetitor.share;
    const competitorNewShare = Math.max(0, Math.min(1, selectedCompetitor.share + competitorShareChange));

    // Generate narrative
    const narrative = generateNarrative(
      selectedCompetitor,
      priceChange,
      volumeChangePercent,
      shareChangePoints,
      t
    );

    return {
      volumeChange: volumeChangePercent,
      shareChange: shareChangePoints,
      revenueChange: revenueChangePercent,
      competitorNewShare,
      competitorShareChange: (competitorNewShare - selectedCompetitor.share) * 100,
      narrative,
    };
  }, [selectedCompetitor, priceChange, t]);

  if (marketLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-text-secondary">
          {t ? 'Cargando datos de competidores...' : 'Loading competitor data...'}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card p-6 bg-gradient-to-r from-warning-500/10 to-surface-700">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🎯</span>
          <div>
            <h2 className="text-xl font-bold text-white">
              {t ? '¿Qué pasa si la competencia cambia precio?' : 'What if competitor changes price?'}
            </h2>
            <p className="text-sm text-text-secondary">
              {t
                ? 'Simula cambios de precio de competidores y ve el impacto en tu portafolio'
                : 'Simulate competitor price changes and see impact on your portfolio'}
            </p>
          </div>
        </div>
      </div>

      {/* Competitor Selector */}
      <div className="card p-6">
        <h3 className="text-sm font-medium text-text-tertiary uppercase tracking-wider mb-4">
          {t ? 'Seleccionar Competidor' : 'Select Competitor'}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {competitors.map((competitor) => (
            <button
              key={competitor.id}
              onClick={() => {
                setSelectedCompetitor(competitor);
                setPriceChange(0);
              }}
              className={`p-4 rounded-lg border-2 transition-all text-left ${
                selectedCompetitor?.id === competitor.id
                  ? 'border-warning-500 bg-warning-500/10'
                  : 'border-surface-600 bg-surface-700/50 hover:border-surface-500'
              }`}
            >
              <div className="font-medium text-white truncate">{competitor.name}</div>
              <div className="text-sm text-text-secondary mt-1" title={t ? 'Participación en valor' : 'Value share'}>
                {(competitor.share * 100).toFixed(1)}% {t ? 'valor' : 'value share'}
              </div>
              {competitor.growth !== undefined && (
                <div className={`text-xs mt-1 ${competitor.growth >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
                  {competitor.growth >= 0 ? '+' : ''}{(competitor.growth * 100).toFixed(1)}% YoY
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Price Change Slider */}
      {selectedCompetitor && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-white">
                {t ? 'Cambio de Precio del Competidor' : 'Competitor Price Change'}
              </h3>
              <p className="text-sm text-text-secondary">
                {t ? `Si ${selectedCompetitor.name} cambia su precio...` : `If ${selectedCompetitor.name} changes their price...`}
              </p>
            </div>
            <span className={`text-2xl font-bold ${
              priceChange > 0 ? 'text-success-400' : priceChange < 0 ? 'text-danger-400' : 'text-text-secondary'
            }`}>
              {priceChange > 0 ? '+' : ''}{priceChange}%
            </span>
          </div>

          {/* Slider */}
          <div className="relative mb-6">
            <input
              type="range"
              min="-30"
              max="30"
              step="1"
              value={priceChange}
              onChange={(e) => setPriceChange(parseInt(e.target.value))}
              className="w-full h-2 bg-surface-600 rounded-lg appearance-none cursor-pointer slider-thumb"
            />
            <div className="flex justify-between text-xs text-text-tertiary mt-2">
              <span>-30%</span>
              <span className="text-warning-400">0%</span>
              <span>+30%</span>
            </div>
          </div>

          {/* Quick Adjustments */}
          <div className="flex flex-wrap gap-2">
            {[-20, -10, -5, 0, 5, 10, 20].map((val) => (
              <button
                key={val}
                onClick={() => setPriceChange(val)}
                className={`px-3 py-1.5 rounded text-sm transition-colors ${
                  priceChange === val
                    ? 'bg-warning-500 text-white'
                    : 'bg-surface-600 text-text-secondary hover:bg-surface-500'
                }`}
              >
                {val > 0 ? '+' : ''}{val}%
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Impact Cards */}
      {selectedCompetitor && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <ImpactCard
            label={t ? 'TU VOLUMEN' : 'YOUR VOLUME'}
            value={impact.volumeChange}
            format="percent"
            positive={impact.volumeChange > 0}
            icon="📦"
          />
          <ImpactCard
            label={t ? 'TU PARTICIPACIÓN (VALOR)' : 'YOUR VALUE SHARE'}
            value={impact.shareChange}
            format="points"
            positive={impact.shareChange > 0}
            icon="📈"
          />
          <ImpactCard
            label={t ? 'TU INGRESO' : 'YOUR REVENUE'}
            value={impact.revenueChange}
            format="percent"
            positive={impact.revenueChange > 0}
            icon="💰"
          />
          <ImpactCard
            label={t ? 'SHARE COMPETIDOR' : 'COMPETITOR SHARE'}
            value={impact.competitorShareChange || 0}
            format="points"
            positive={(impact.competitorShareChange || 0) < 0}
            subtitle={`${selectedCompetitor.name} (${t ? 'valor' : 'value'})`}
            icon="🎯"
          />
        </div>
      )}

      {/* Scenario Narrative */}
      {selectedCompetitor && impact.narrative && (
        <div className="card p-6 border-l-4 border-l-warning-500">
          <div className="flex items-start gap-3">
            <span className="text-xl">💡</span>
            <div className="space-y-3">
              <h3 className="font-semibold text-white">
                {t ? 'Escenario' : 'Scenario'}: {impact.narrative.title}
              </h3>
              <ul className="space-y-2">
                {impact.narrative.points.map((point, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-text-secondary">
                    <span className="text-warning-400 mt-0.5">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              <div className="pt-3 border-t border-surface-600">
                <h4 className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                  {t ? 'Recomendación' : 'Recommendation'}
                </h4>
                <p className="text-sm text-primary-400">{impact.narrative.recommendation}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!selectedCompetitor && (
        <div className="card p-8 text-center">
          <span className="text-4xl mb-4 block">👆</span>
          <p className="text-text-secondary">
            {t
              ? 'Selecciona un competidor arriba para comenzar la simulación'
              : 'Select a competitor above to start the simulation'}
          </p>
        </div>
      )}
    </div>
  );
}

// Impact Card Component
function ImpactCard({
  label,
  value,
  format,
  positive,
  subtitle,
  icon,
}: {
  label: string;
  value: number;
  format: 'percent' | 'points';
  positive: boolean;
  subtitle?: string;
  icon: string;
}) {
  const formattedValue = format === 'percent'
    ? `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`
    : `${value >= 0 ? '+' : ''}${value.toFixed(2)} pp`;

  const colorClass = value === 0
    ? 'text-text-secondary'
    : positive
    ? 'text-success-400'
    : 'text-danger-400';

  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">{icon}</span>
        <span className="text-xs text-text-tertiary uppercase tracking-wider">{label}</span>
      </div>
      <div className={`text-2xl font-bold ${colorClass}`}>
        {formattedValue}
      </div>
      {subtitle && (
        <div className="text-xs text-text-tertiary mt-1 truncate">{subtitle}</div>
      )}
    </div>
  );
}

// Generate narrative based on scenario
function generateNarrative(
  competitor: Competitor,
  priceChange: number,
  volumeChange: number,
  shareChange: number,
  spanish: boolean
): { title: string; points: string[]; recommendation: string } {
  const isIncrease = priceChange > 0;
  const magnitude = Math.abs(priceChange) >= 15 ? 'significant' : Math.abs(priceChange) >= 5 ? 'moderate' : 'minor';

  if (spanish) {
    const title = isIncrease
      ? `${competitor.name} sube precios ${Math.abs(priceChange)}%`
      : `${competitor.name} baja precios ${Math.abs(priceChange)}%`;

    const points = isIncrease
      ? [
          `Tu volumen aumenta ~${volumeChange.toFixed(1)}% por migración de consumidores`,
          `Ganas aproximadamente ${shareChange.toFixed(2)} puntos de participación`,
          magnitude === 'significant'
            ? 'Oportunidad significativa de capturar consumidores sensibles al precio'
            : 'Impacto moderado en switching de consumidores',
          `${competitor.name} probablemente perderá consumidores del segmento valor`,
        ]
      : [
          `Tu volumen disminuye ~${Math.abs(volumeChange).toFixed(1)}% por migración hacia ${competitor.name}`,
          `Pierdes aproximadamente ${Math.abs(shareChange).toFixed(2)} puntos de participación`,
          magnitude === 'significant'
            ? 'Riesgo significativo - consumidores sensibles al precio migrarán'
            : 'Impacto moderado - principalmente segmento valor afectado',
          'Tu segmento premium está relativamente protegido (menor cross-elasticity)',
        ];

    const recommendation = isIncrease
      ? 'Considera mantener precios para capturar volumen migrado. Evalúa promociones tácticas para acelerar switching.'
      : 'Considera respuesta promocional en SKUs afectados. Monitorea impacto en segmento premium antes de reaccionar con precio.';

    return { title, points, recommendation };
  }

  // English version
  const title = isIncrease
    ? `${competitor.name} raises prices by ${Math.abs(priceChange)}%`
    : `${competitor.name} lowers prices by ${Math.abs(priceChange)}%`;

  const points = isIncrease
    ? [
        `Your volume increases ~${volumeChange.toFixed(1)}% from consumer migration`,
        `You gain approximately ${shareChange.toFixed(2)} share points`,
        magnitude === 'significant'
          ? 'Significant opportunity to capture price-sensitive consumers'
          : 'Moderate impact on consumer switching',
        `${competitor.name} will likely lose value-segment consumers`,
      ]
    : [
        `Your volume decreases ~${Math.abs(volumeChange).toFixed(1)}% from migration to ${competitor.name}`,
        `You lose approximately ${Math.abs(shareChange).toFixed(2)} share points`,
        magnitude === 'significant'
          ? 'Significant risk - price-sensitive consumers will switch'
          : 'Moderate impact - mainly value segment affected',
        'Your premium segment is relatively protected (lower cross-elasticity)',
      ];

  const recommendation = isIncrease
    ? 'Consider maintaining prices to capture migrating volume. Evaluate tactical promotions to accelerate switching.'
    : 'Consider promotional response in affected SKUs. Monitor premium segment impact before reacting with price.';

  return { title, points, recommendation };
}

export default CompetitorMoveSimulator;
